import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { motion, useAnimationControls, useReducedMotion, type Transition } from 'framer-motion'
import {
  ChatInput,
  useActiveChatId,
  useDictatedText,
  useCurrentChatInput,
  useSetChatInput,
  getCurrentChatInput,
  useGetRealChatId,
} from '@renderer/chat'
import { useSubmitMessage } from '@renderer/chat/hooks'
import { useChatInputPulse } from '../../hooks/use-chat-input-pulse'
import { GlobalEvents } from '@highlight/ipc'
import { ChatInputElementId } from '@renderer/chat/components'
import { contextSourcesAtom } from '@renderer/chat/store/atoms'
import { twMerge } from 'tailwind-merge'
import { useAtomValue, useSetAtom } from 'jotai'
import { InSidepanelContext, type SidepanelState } from '@renderer/overlay/atoms'
import { useIsOnboarded } from '@renderer/hooks/useIsOnboarded'
import { useAutoInsertPermission } from '@renderer/hooks/settings/useAutoInsertPermission'
import OverlayIpcRenderer from '@renderer/ipc/OverlayIpc.renderer'
import {
  stagedActionEditStatesAtom,
  useChatInputPlaceholder,
  useSetChatInputPlaceholder,
  useSidepanelFindInPageOpen,
} from '../../atoms'
import { useViewActions, useViewNavigation } from '../../view-atoms'
import { isViewPopout } from '../../surface'
import { viewChrome } from '../../view-registry'
import { ActiveChats } from './nested/active-chats'
import { ResumeChatBar } from './nested/resume-chat-bar'
import { viewContextOf } from '../../view-definitions'
import { useEditStagedAction } from '../../hooks/use-staged-actions'
import { useChatInputMode } from '../../hooks/use-chat-input-mode'
import { useSubmitStagedActionMessage } from '../../hooks/use-submit-staged-action-message'
import { useSubmitChatOutputMessage } from '../../hooks/use-submit-chat-output-message'
import { MEETING_VIEW_ID } from '../meeting/meeting-view-id'
import { useStartMeetingChat } from '../../views/meeting-view/use-meeting-chat-drawer'
import { useFocusedWindowSnapshot } from '@renderer/hooks/use-focused-window'
import * as Sentry from '@sentry/electron/renderer'

// Module-level so the element refs stay stable: `ChatInput` is memoized, and a
// fresh element per render would defeat that and re-render the whole composer
// subtree every time this component re-renders (view nav, chat mode changes).
// Both take no props, so one element each serves forever.
const ACTIVE_CHATS_HEADER = <ActiveChats />
const RESUME_CHAT_HEADER = <ResumeChatBar />
const EMPTY_STAGED_ACTION_IDS: ReadonlyArray<string> = []

/**
 * One quick breath of the whole input — transform only, so it stays
 * compositor-friendly. Springs support only two keyframes, so the pulse jumps
 * to its peak and springs back, slightly underdamped for a lively settle.
 */
const pulseTransition: Transition = { type: 'spring', stiffness: 380, damping: 26 }
const pulseScale = [1.015, 1]

interface SidepanelChatInputProps {
  sidepanelState: SidepanelState
}

export const SidepanelChatInput = memo(function SidepanelChatInput({ sidepanelState }: SidepanelChatInputProps) {
  const { topView, canGoBack: isViewOpen } = useViewNavigation()
  const topViewChrome = viewChrome(topView)
  // Unlike `ACTIVE_CHATS_HEADER` above, this slot takes the open view's own
  // context (a meeting's permissions card needs none of Home's props, but the
  // registry contract is per-view), so it can't be a module-level constant.
  // `ChatInput` is still memoized, so build it with `useMemo` rather than a
  // fresh element every render — same fresh-element trap, different cause.
  const chatInputBeforeRender = topViewChrome?.chatInputBefore
  const chatInputBefore = useMemo(
    () => chatInputBeforeRender?.({ id: topView.id, context: topView.context }),
    [chatInputBeforeRender, topView.id, topView.context],
  )
  // Full-list views keep the input full-width (they read as an extension of
  // Home); detail views inset it to focus the conversation on that view.
  // A popout's root entry has no back stack, so `isViewOpen` is false there,
  // but the window is itself a focused detail view and is always inset.
  const isChatInputInset = isViewPopout || (isViewOpen && !topViewChrome?.fullWidthChatInput)
  const isChatViewOpen = topView.id === 'chat'
  // Chatting with a meeting stays on the meeting: the thread opens in this
  // entry's drawer instead of stacking a Chat View, and follow-ups target the
  // conversation it already holds (so a dismissed thread is resumed, not
  // replaced) rather than forcing a new chat the way an unset id would.
  // Resolved through the temp → real mapping, not read raw: the entry latches
  // the UUID its submit minted, and a follow-up sent before the rename lands
  // would carry an id the backend never issued.
  const getRealChatId = useGetRealChatId()
  const meetingContext = viewContextOf(topView, MEETING_VIEW_ID)
  const meetingChatId = meetingContext?.chatId ? getRealChatId(meetingContext.chatId) : undefined
  // A meeting with no thread yet opens its first chat under the id its note keeps (see `useMeetingChatThread`).
  const newMeetingChatId = meetingChatId ? undefined : meetingContext?.newChatId
  const startMeetingChat = useStartMeetingChat()
  const findInPageOpen = useSidepanelFindInPageOpen()
  const activeChatId = useActiveChatId()
  // Inside a Chat View, fall back to the globally-active chat while the view's
  // own context has no id: a New Chat view holds `chatId` unset from submit
  // until the real backend id lands (id reconciliation skips the temp swap), and
  // without this the Stop button — gated on this prop — misses that window and
  // Mentions holds the slot instead. Mirrors `currentChatId` in
  // use-chat-id-reconciliation. Outside a Chat View it stays unset, so Stop
  // never appears on Home for a background stream.
  const activeChatViewId = isChatViewOpen ? (viewContextOf(topView, 'chat')?.chatId ?? activeChatId) : undefined
  const { focusedWindow } = useFocusedWindowSnapshot()
  const focusedWindowRef = useRef(focusedWindow)
  useEffect(() => {
    focusedWindowRef.current = focusedWindow
  }, [focusedWindow])

  const { openView } = useViewActions()
  const { editStagedAction } = useEditStagedAction()
  const chatInputMode = useChatInputMode()
  const editStates = useAtomValue(stagedActionEditStatesAtom)
  // Scoped edits open the action's chat drawer (HIG-5977): the hook submits on
  // the action's conversation and flips the open Action View entry into its
  // chat state, sliding the thread up over the view while the draft
  // regenerates. It falls back to the edit endpoint (in place, no drawer) for
  // conversation-less drafts. Active both ways because the drawer overlays the
  // Action View: follow-ups sent from it stay edits to the same action.
  const submitStagedActionMessage = useSubmitStagedActionMessage(chatInputMode.stagedActionId, editStagedAction)
  const submitChatOutputMessage = useSubmitChatOutputMessage(
    chatInputMode.conversationId,
    chatInputMode.stagedActionIds ?? EMPTY_STAGED_ACTION_IDS,
  )

  // The draft itself rides on the input as an attachment pill, registered by
  // the Action View (`useActionChatAttachment`).
  const isStagedActionMode = chatInputMode.mode === 'staged-action'
  const isChatOutputMode = chatInputMode.mode === 'chat-output'
  const composerChatId = isChatOutputMode ? chatInputMode.conversationId : (meetingChatId ?? activeChatViewId)

  const setChatInput = useSetChatInput()
  const submitMessage = useSubmitMessage()

  const isBriefMode = chatInputMode.mode === 'brief'
  const briefConversationId = chatInputMode.briefConversationId
  const submitToBriefConversation = useCallback(async () => {
    if (!briefConversationId) return
    const input = getCurrentChatInput()
    if (!input.trim()) return
    setChatInput('')

    await submitMessage(input, {
      conversationId: briefConversationId,
      // Brief follow-ups are still a chat about this meeting, so they land in
      // the Meeting View's drawer like every other meeting-scoped thread.
      onSubmit: (conversationId: string) => startMeetingChat(conversationId),
    })
  }, [briefConversationId, submitMessage, setChatInput, startMeetingChat])

  const isChatInputLoading =
    isStagedActionMode && chatInputMode.stagedActionId
      ? editStates[chatInputMode.stagedActionId]?.status === 'pending'
      : false

  const chatInputPlaceholderOverride = useChatInputPlaceholder()
  const setChatInputPlaceholder = useSetChatInputPlaceholder()

  // "New chat" invoked while the new chat is already open (Cmd+N, toolbar menu):
  // nothing navigates, so acknowledge the press by scale-pulsing the input
  // (`start` restarts the keyframes, so a rapid second press re-pulses). Focus
  // only when asked (the hotkey) — the menu path returns focus to its trigger
  // on close, and focusing here would fight that.
  const pulseControls = useAnimationControls()
  const reduceMotion = useReducedMotion()
  useChatInputPulse((options) => {
    if (!reduceMotion) void pulseControls.start({ scale: pulseScale }, pulseTransition)
    if (options?.focus) document.getElementById(ChatInputElementId)?.focus()
  })

  const inputPlaceholder = useMemo(() => {
    if (chatInputPlaceholderOverride) return chatInputPlaceholderOverride
    if (isBriefMode) return 'Ask a follow-up about this brief\u2026'
    // The per-view wording lives in the registry alongside `fullWidthChatInput`
    // \u2014 both are chat-input chrome, and a view that wants one usually wants the
    // other considered too.
    return topViewChrome?.chatInputPlaceholder ?? 'Ask Highlight anything'
  }, [chatInputPlaceholderOverride, topViewChrome, isBriefMode])

  const handleChatSubmit = useCallback(
    (conversationId: string) => {
      setChatInputPlaceholder(null)
      if (isChatViewOpen) return
      if (topView.id === MEETING_VIEW_ID) {
        startMeetingChat(conversationId)
        return
      }
      // Submitting from a drill-in View stacks Chat *on top of it*, so Home is
      // only the source when Home is what's open. Otherwise leave it unset and
      // let the back button name the parent entry itself.
      openView('chat', { chatId: conversationId }, isViewOpen ? undefined : { sourceLabel: 'Home' })
    },
    [openView, isChatViewOpen, isViewOpen, setChatInputPlaceholder, topView.id, startMeetingChat],
  )

  const submitToNewMeetingChat = useCallback(() => {
    if (!newMeetingChatId) return
    const input = getCurrentChatInput()
    if (!input.trim()) return
    void submitMessage(input, { isNewChat: true, newConversationId: newMeetingChatId, onSubmit: handleChatSubmit })
  }, [newMeetingChatId, submitMessage, handleChatSubmit])

  // --- Window-scoped effects ---
  const setSources = useSetAtom(contextSourcesAtom)
  const isOnboarded = useIsOnboarded()
  const { dictatedText: pushToTalkDictation, clearDictatedText: clearPushToTalkDictation } = useDictatedText('pttKey')
  const chatInput = useCurrentChatInput()
  const { isAutoInsertEnabled } = useAutoInsertPermission()

  // Fetch window list when sidepanel opens and on focused window changes
  useEffect(() => {
    if (sidepanelState !== 'open') return

    void OverlayIpcRenderer.getScreenshotWindows()
      .then((windows) => setSources((prev) => ({ ...prev, windows })))
      .catch((error) => {
        Sentry.captureException(error)
      })
  }, [focusedWindow?.pid, focusedWindow?.windowTitle, sidepanelState, setSources])

  // Handle PTT dictation
  useEffect(() => {
    if (!pushToTalkDictation || !isOnboarded) return

    async function insertIntoFocusedWindow(textToInsert: string) {
      const fw = focusedWindowRef.current
      if (!fw) return
      await OverlayIpcRenderer.typeText({ text: textToInsert, pid: fw.pid, method: 'paste', html: textToInsert })
      clearPushToTalkDictation()
    }

    if (isAutoInsertEnabled) {
      insertIntoFocusedWindow(pushToTalkDictation)
    } else if (sidepanelState === 'open') {
      setChatInput(chatInput ? chatInput + ' ' + pushToTalkDictation : pushToTalkDictation)
      setTimeout(() => {
        GlobalEvents.emit('chatInput:moveCursorToEnd', ChatInputElementId)
      }, 20)
      clearPushToTalkDictation()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pushToTalkDictation, isOnboarded, isAutoInsertEnabled, sidepanelState])

  return (
    <motion.div
      // The whole composer, not the text field inside it, is what a tour points
      // at — the buttons on its right are part of what the step describes.
      data-tour-section="chat-input"
      // No framer layout animation here: the footer is bottom-anchored so the
      // input never moves from content above it, and a layout projection
      // fighting the CSS transitions is what made the resize stutter. CSS
      // margin/min-height transitions own the whole shrink/expand. The
      // `animate` controls drive only the pulse's transform.
      animate={pulseControls}
      className={twMerge(
        'relative z-10 rounded-2xl transition-[margin] duration-[250ms] ease-[cubic-bezier(0.32,0.72,0,1)]',
        // Overlay detail views inset the composer on the card. Popouts (`app:`)
        // take the same side inset; bottom spacing is the window column padding.
        isChatInputInset && 'mx-3 mb-3',
        'app:mx-1 app:mb-0',
      )}
    >
      <InSidepanelContext.Provider value={true}>
        <ChatInput
          chatId={composerChatId}
          contextBarClassName="p-0"
          hideScrollToEnd
          // Home's full-width input lists in-flight chats; the inset variant has
          // no room for that list, but carries the single "Resume chat" row for
          // a view with a dismissed thread — an Action View's edit or a Meeting
          // View's chat (it renders null everywhere else).
          header={isChatInputInset ? RESUME_CHAT_HEADER : ACTIVE_CHATS_HEADER}
          before={chatInputBefore}
          // The full-width composer is edge to edge; inset the notices so they
          // sit on it the way they do on the inset card.
          noticesClassName={isChatInputInset ? undefined : 'mx-3'}
          className={twMerge(
            isChatInputInset && '!min-h-0 [&_.flex.items-end]:!py-2 [&_.flex.items-end]:!pr-1.5 [&_div]:!min-h-0',
            // The scoped input is about the attached draft — screen context is
            // noise here, so its pill hides while the Action View is open.
            isStagedActionMode && '[&_[data-window-context]]:hidden',
          )}
          placeholder={isStagedActionMode ? 'Ask Highlight to change this action…' : inputPlaceholder}
          onSubmit={handleChatSubmit}
          handleSubmit={
            isStagedActionMode
              ? submitStagedActionMessage
              : isChatOutputMode
                ? submitChatOutputMessage
                : isBriefMode
                  ? submitToBriefConversation
                  : newMeetingChatId
                    ? submitToNewMeetingChat
                    : undefined
          }
          // An action without a conversation is edited through its own endpoint,
          // so typing here may never reach a chat send.
          prefetchesSendChallenge={!isStagedActionMode}
          isLoading={isChatInputLoading}
          typeToFocusDisabled={findInPageOpen}
        />
      </InSidepanelContext.Provider>
    </motion.div>
  )
})
